import ListCard from '@Components/ListCard/ListCard';
import AssetLogService from '@Services/AssetLog.services.ts';
import { AssetLog } from '@Types/Asset.types';
import { PaginatedResponse } from '@Types/Response.types';
import { PAGE_PROP, PAGE_SIZE, PAGINATED_DEFAULT, PATHS, SEARCH_PROP, TABLE_STRUCTURES } from '@Utils/constants';
import { formatScannerLogListData } from '@Utils/format.utils.tsx';
import { handleRequestError } from '@Utils/request.utils';
import { Pagination, SearchBox, SectionCard, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const ScannerLog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assetLogsInfo, setAssetLogsInfo] = useState<PaginatedResponse<AssetLog>>(PAGINATED_DEFAULT);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const currentPage = Number(searchParams.get(PAGE_PROP) ?? '1');
  const searchQuery = searchParams.get(SEARCH_PROP) ?? '';

  useTopHat({
    activeNav: 'nav-reports',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      { title: 'Reports', href: PATHS.REPORTS, id: 'Reports' },
      {
        title: 'Scanner Log',
        href: PATHS.SCANNER_LOG,
        id: 'Scanner Log',
      },
    ],
  });

  useEffect(() => {
    setIsLoading(true);
    AssetLogService.getAssetLogsSearch({ search: searchQuery }, currentPage)
      .then(({ data }) => {
        setAssetLogsInfo(data);
        setErrorMessage(undefined);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => setIsLoading(false));
  }, [searchQuery, currentPage]);

  const handleSearchQueryChange = (newValue: string) => {
    searchParams.set(SEARCH_PROP, newValue);
    searchParams.set(PAGE_PROP, '1');
    if (!newValue) {
      searchParams.delete(SEARCH_PROP);
    }
    setSearchParams(searchParams);
  };

  const handleNewPage = (newPage: number) => {
    if (newPage <= 0 || newPage > assetLogsInfo.totalPages) {
      return;
    }
    searchParams.set(PAGE_PROP, newPage.toString());
    setSearchParams(searchParams);
  };

  return (
    <div className='grid'>
      <div className='col-12 col-md-3'>
        <SectionCard>
          <SearchBox
            id='search-scanner-logs'
            fullWidth
            focusOnRender
            value={searchQuery}
            defaultValue={searchQuery}
            onChange={handleSearchQueryChange}
          />
        </SectionCard>
      </div>
      <div className='col-12 col-md-9'>
        <ListCard
          title="Scanner Log"
          structure={TABLE_STRUCTURES.SCANNER_LOG}
          data={formatScannerLogListData(assetLogsInfo)}
          detailsPath={PATHS.ASSETS}
          loading={isLoading}
          withDetails
          error={errorMessage}
          searchEntered={!!searchQuery}
        />
      </div>
      <div className='col-12 content-middle content-right-md'>
        <Pagination
          count={assetLogsInfo.totalDocs}
          perPage={PAGE_SIZE}
          currentPage={currentPage - 1}
          onChange={(page) => handleNewPage(page + 1)}
        />
      </div>
    </div>
  );
};

export default ScannerLog;
